/**
 * The organizations on this server - the host operator's home page.
 *
 * Each organization (a tenant) is a separate MSP with its own database: its own staff, customers,
 * devices, settings, mail account and backups. Nothing here shows any organization's data; a host
 * operator who needs to work inside one ENTERS it, and from then on the app is that organization's
 * CRM with them as its administrator, under a banner that says so and offers the way back.
 *
 * Suspending stops an organization rather than hiding it: its people are refused at sign-in, its
 * agents at connect, and resuming starts it again exactly as it was. Deleting moves its folder aside
 * on the server rather than erasing it, and wants the name typed.
 *
 * An operator sees only the organizations they are assigned to, and the buttons that change one
 * appear only with the Manage organizations permission. The operators themselves are on Users.
 */
import type { Container, StackPanel, WrapPanel } from 'webwin';
import * as auth from '../api/auth.js';
import * as host from '../api/host.js';
import * as hostAccess from '../api/hostAccess.js';
import { describeError } from '../api/http.js';
import { HostPermission, TenantStatus, type HostEventDto, type TenantDto } from '../contracts.gen.js';
import { join, localDate, localDateTime, plural } from '../format.js';
import { CardList } from '../ui/CardList.js';
import type { Card } from '../ui/Card.js';
import { chip } from '../ui/Chip.js';
import { ask, confirm } from '../ui/dialogs.js';
import { EmptyState } from '../ui/EmptyState.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  field,
  fieldRow,
  fillWith,
  FONT,
  GAP,
  h1,
  h2,
  muted,
  passwordBox,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack
} from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';

export function organizationsPage(): CrmPage {
  return {
    mount(host_: Container, ctx: PageContext) {
      let busy = false;
      const status = new StatusLine();
      const manage = (): boolean => hostAccess.has(HostPermission.ManageOrganizations);

      // ---- header ----
      const titleRow = splitRow({ align: 'center' });
      fillWith(titleRow, h1('Organizations'));
      const newButton = button('New organization', () => openEditor(), true);
      newButton.visible = manage();
      titleRow.controls.add(newButton);
      const header = stackOf(
        vstack({ spacing: GAP.xs }),
        titleRow,
        muted(
          'Each organization is a separate MSP on this server, with its own staff, customers, devices and settings. Open one to work in it as an administrator or a technician, as your assignment says.'
        )
      );

      // ---- new organization ----
      const nameBox = textBox({ placeholder: 'Acme IT Services' });
      const adminNameBox = textBox({ placeholder: 'Their name' });
      const adminEmailBox = textBox({ placeholder: 'admin@example.com' });
      const passwordField = passwordBox();
      const notesBox = textBox();
      const editorStatus = new StatusLine();
      const editor = card({ visible: false });
      editor.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('New organization'),
          field('Name', nameBox),
          fieldRow(field('Administrator', adminNameBox), field('Administrator email', adminEmailBox)),
          field('Temporary password', passwordField),
          muted(
            'The administrator signs in with this password and must choose their own straight away. An email address can belong to one organization on this server only.'
          ),
          field('Notes (only host operators see these)', notesBox),
          stackOf(buttonRow(), button('Create', () => void create(), true), button('Cancel', () => closeEditor())),
          editorStatus.label
        )
      );

      // ---- organizations ----
      const list = new CardList<TenantDto>({ key: t => t.id, version: t => [t, manage()], build: t => buildTenantCard(t) });
      const empty = new EmptyState('');

      // ---- recent activity ----
      const eventList = new CardList<HostEventDto>({ key: e => e.id, build: e => buildEventRow(e) });
      const activity = card();
      activity.controls.add(
        stackOf(vstack({ spacing: GAP.sm }), h2('Recent host activity'), eventList.panel)
      );

      const { view, content } = scrollPage();
      content.controls.add(
        stackOf(
          vstack({ spacing: GAP.lg }),
          header,
          status.label,
          editor,
          empty.panel,
          list.panel,
          activity
        )
      );
      host_.controls.add(view);

      // ---------------------------------------------------------------

      function buildTenantCard(t: TenantDto): Card {
        const heading = buttonRow({ spacing: GAP.sm });
        heading.controls.add(value(t.name, { font: FONT.bold }));
        if (t.isDefault) heading.controls.add(chip('default', 'brand'));
        if (t.status === TenantStatus.Suspended) heading.controls.add(chip('suspended', 'warn'));
        else if (t.startError) heading.controls.add(chip('did not start', 'danger'));

        const running = t.customers !== null && t.customers !== undefined;
        const body = stackOf(vstack({ spacing: GAP.xs }), heading);
        body.controls.add(
          text(
            running
              ? join([
                  plural(t.customers ?? 0, 'customer'),
                  plural(t.devices ?? 0, 'device'),
                  plural(t.staffUsers ?? 0, 'staff account')
                ])
              : 'Not running',
            { font: FONT.small }
          )
        );
        body.controls.add(muted(join([`Organization ${t.id}`, t.slug, `created ${localDate(t.createdUtc)}`])));
        if (t.status === TenantStatus.Suspended && t.suspendedReason) body.controls.add(muted('Suspended: ' + t.suspendedReason, 'warn'));
        if (t.startError) body.controls.add(muted('Start failed: ' + t.startError, 'danger'));
        if (t.notes) body.controls.add(muted(t.notes));

        const actions = buttonRow();
        if (running) actions.controls.add(button('Open', () => void enter(t), true));
        if (manage()) addManageButtons(actions, t);
        body.controls.add(actions);

        const shell = card();
        shell.controls.add(body);
        return shell;
      }

      function addManageButtons(actions: StackPanel | WrapPanel, t: TenantDto): void {
        actions.controls.add(button('Rename', () => void rename(t)));
        if (!t.isDefault) {
          actions.controls.add(
            t.status === TenantStatus.Suspended
              ? button('Resume', () => void setSuspended(t, false))
              : button('Suspend', () => void setSuspended(t, true))
          );
        }
        if (t.status === TenantStatus.Active) actions.controls.add(button('Restart', () => void restart(t)));
        if (!t.isDefault) actions.controls.add(button('Delete', () => void remove(t)));
      }

      function buildEventRow(e: HostEventDto): Card {
        const body = stackOf(
          vstack({ spacing: 0 }),
          text(e.summary, { font: FONT.small }),
          muted(join([localDateTime(e.whenUtc), e.actor, e.detail ?? null]))
        );
        const shell = card({ tint: 'alt' });
        shell.controls.add(body);
        return shell;
      }

      // ---- actions ----

      function openEditor(): void {
        nameBox.text = '';
        adminNameBox.text = '';
        adminEmailBox.text = '';
        passwordField.text = '';
        notesBox.text = '';
        editorStatus.clear();
        editor.visible = true;
        relayout(content);
        nameBox.focus();
      }

      function closeEditor(): void {
        editor.visible = false;
        relayout(content);
      }

      async function create(): Promise<void> {
        if (busy) return;
        busy = true;
        editorStatus.busy('Creating the organization…');
        try {
          const { tenant, warning } = await host.createTenant({
            name: nameBox.text.trim(),
            slug: null,
            adminEmail: adminEmailBox.text.trim(),
            adminName: adminNameBox.text.trim(),
            adminPassword: passwordField.text,
            notes: notesBox.text.trim() || null
          });
          if (ctx.cancelled()) return;
          passwordField.text = '';
          closeEditor();
          if (warning) status.error(warning);
          else status.ok(`Created ${tenant.name}. Its administrator can sign in now.`);
          await refresh();
        } catch (error) {
          // The form stays open with what was typed, so a taken address is fixed rather than retyped.
          if (!ctx.cancelled()) editorStatus.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function enter(t: TenantDto): Promise<void> {
        if (busy) return;
        busy = true;
        status.busy(`Opening ${t.name}…`);
        try {
          await auth.enterTenant(t.id);
          // The shell hears the session change and rebuilds the rail for the organization.
          ctx.go('/dashboard', { replace: true });
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function rename(t: TenantDto): Promise<void> {
        const name = await ask(ctx.desktop, { title: 'Rename organization', prompt: 'New name', initial: t.name, okLabel: 'Rename' });
        if (name === null || ctx.cancelled() || name.trim() === t.name) return;
        await act(`Renaming ${t.name}…`, async () => {
          const saved = await host.updateTenant(t.id, { name: name.trim(), notes: null });
          return `Renamed to ${saved.name}.`;
        });
      }

      async function setSuspended(t: TenantDto, suspend: boolean): Promise<void> {
        if (suspend) {
          const reason = await ask(ctx.desktop, {
            title: `Suspend ${t.name}`,
            prompt:
              'Its people will be refused at sign-in and its agents at connect, and its background work stops. Nothing is deleted, and Resume starts it again as it was. Reason (optional, shown to them):',
            okLabel: 'Suspend'
          });
          if (reason === null || ctx.cancelled()) return;
          await act(`Suspending ${t.name}…`, async () => {
            await host.setSuspended(t.id, true, reason.trim() || null);
            return `${t.name} is suspended.`;
          });
        } else {
          await act(`Resuming ${t.name}…`, async () => {
            await host.setSuspended(t.id, false, null);
            return `${t.name} is running again.`;
          });
        }
      }

      async function restart(t: TenantDto): Promise<void> {
        const ok = await confirm(ctx.desktop, {
          title: `Restart ${t.name}`,
          message:
            'Closes and reopens its database and restarts its background work - which also applies a restore staged from its Backups settings. Its people may see a request fail for a moment.'
        });
        if (!ok || ctx.cancelled()) return;
        await act(`Restarting ${t.name}…`, async () => {
          await host.restartTenant(t.id);
          return `${t.name} restarted.`;
        });
      }

      async function remove(t: TenantDto): Promise<void> {
        const typed = await ask(ctx.desktop, {
          title: `Delete ${t.name}`,
          prompt: `This removes the organization from the server: its people can no longer sign in and its agents are refused. Its files are moved aside on the server, not erased. Type ${t.name} to confirm.`,
          okLabel: 'Delete'
        });
        if (typed === null || ctx.cancelled()) return;
        await act(`Deleting ${t.name}…`, async () => {
          await host.deleteTenant(t.id, typed);
          return `Deleted ${t.name}. Its files were kept on the server.`;
        });
      }

      async function act(working: string, work: () => Promise<string>): Promise<void> {
        if (busy) return;
        busy = true;
        status.busy(working);
        try {
          const done = await work();
          if (ctx.cancelled()) return;
          status.ok(done);
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
          await refresh();
        } finally {
          busy = false;
        }
      }

      async function refresh(): Promise<void> {
        try {
          const signal = ctx.dispose.signal();
          const [ts, es] = await Promise.all([host.tenants(signal), host.events(30, signal)]);
          if (ctx.cancelled()) return;
          list.set(ts);
          empty.message = manage() ? 'No organizations yet.' : 'You are not assigned to any organization. Ask an operator who manages operators.';
          empty.update(ts.length);
          newButton.visible = manage();
          eventList.set(es);
          relayout(content);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not load the organizations: ' + describeError(error));
        }
      }

      // The operator's access may arrive after the page: the buttons follow it.
      ctx.dispose.add(hostAccess.onChanged(() => void refresh()));
      void refresh();
    }
  };
}
